import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import BtcLogo from "../components/BtcLogo";

/**
 * BTC Email · Batch 2 — password-reset completion page.
 *
 * The reset token arrives via the `?token=…` query parameter from the
 * BTC Portal reset email.  The page validates it against the backend
 * on submit; invalid or expired tokens surface a non-enumerating
 * error.  No portal content is accessible until the reset succeeds.
 */
export default function ResetPassword() {
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const [errorMsg, setErrorMsg] = useState("");

  const canSubmit =
    !!token && password.length >= 8 && password === confirm && status !== "sending";

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (r.status === 400) {
        setStatus("error");
        setErrorMsg(
          "This reset link is invalid or has expired. Please request a new one."
        );
        return;
      }
      if (!r.ok) throw new Error("request_failed");
      setStatus("ok");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again in a moment.");
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center px-4">
      <div
        className="bg-white rounded-3xl border border-gray-100 shadow-sm w-full max-w-md p-8 lg:p-10"
        data-testid="reset-password-page"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <BtcLogo className="h-12 w-auto" />
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B] mb-2">
          Set a new password
        </h1>
        {status === "ok" ? (
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 mt-2"
            role="status"
            data-testid="reset-password-ok"
          >
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-700 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold mb-1">
                  Your password has been updated.
                </div>
                <p>
                  All other signed-in sessions have been signed out. Redirecting
                  you to sign in now.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="reset-password-form"
            action="/api/auth/reset-password"
            className="space-y-4"
          >
            <label className="block">
              <span className="text-sm font-medium text-slate-800">
                New password
              </span>
              <input
                type="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 focus:border-[#73B726] focus:outline-none focus:ring-2 focus:ring-[#73B726]/30"
                data-testid="reset-password-new"
              />
              <span className="text-xs text-slate-500">
                Use at least 8 characters.
              </span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-800">
                Confirm new password
              </span>
              <input
                type="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 focus:border-[#73B726] focus:outline-none focus:ring-2 focus:ring-[#73B726]/30"
                data-testid="reset-password-confirm"
              />
            </label>
            {!token && (
              <p
                className="text-sm text-amber-700"
                data-testid="reset-password-missing-token"
              >
                This page needs a reset link to work. Please open the link from
                your BTC Portal reset email.
              </p>
            )}
            {status === "error" && (
              <p
                className="text-sm text-red-600"
                role="alert"
                data-testid="reset-password-error"
              >
                {errorMsg}
              </p>
            )}
            <button
              type="submit"
              disabled={!canSubmit}
              className="btc-btn-primary inline-flex items-center gap-2 w-full justify-center disabled:opacity-60"
              data-testid="reset-password-submit"
            >
              Set new password
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}
        <div className="mt-6 text-sm text-slate-500">
          <Link to="/login" className="underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
